<template>
  <div class="ur-login">
    <!-- ── Main body ── -->
    <div class="ur-login-body">
      <div class="ur-login-grid">

        <!-- Left panel: logo + welcome -->
        <div class="ur-login-left">
          <img :src="logoUrl" class="ur-login-logo-img" alt="logo" />
          <div class="ur-login-welcome">{{ $t('URBANRENEWAL_SELECT.LOGIN.WELCOME') }}</div>
          <!-- 副標題：依需求暫時隱藏，保留備用 -->
          <!-- <div class="ur-sel-login-desc">{{ $t('URBANRENEWAL_SELECT.LOGIN.SYS_DESC') }}</div> -->
        </div>

        <!-- Right panel: reset password card -->
        <div class="ur-login-card">
          <h2 class="ur-login-card-title">{{ $t('URBANRENEWAL_SELECT.RESET.TITLE') }}</h2>

          <template v-if="!successMsg">
            <!-- Error message -->
            <div v-if="errorMsg" class="ur-login-error">{{ errorMsg }}</div>

            <!-- New password field -->
            <div class="ur-login-field">
              <label class="ur-login-field-label">{{ $t('URBANRENEWAL_SELECT.RESET.NEW_PWD') }}</label>
              <div class="ur-login-field-row">
                <span class="ur-login-field-icon">
                  <!-- Lock icon -->
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="5" y="11" width="14" height="10" rx="2"/>
                    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                  </svg>
                </span>
                <input
                  class="ur-login-field-input"
                  :type="showPwd ? 'text' : 'password'"
                  v-model="password"
                  autocomplete="new-password"
                  @keyup.enter="submit"
                  @input="errorMsg = ''"
                />
                <button class="ur-login-toggle-btn" type="button" @click="togglePwd" tabindex="-1">
                  <!-- Eye open -->
                  <svg v-if="showPwd" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <!-- Eye closed -->
                  <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Confirm password field -->
            <div class="ur-login-field ur-login-field-pwd">
              <label class="ur-login-field-label">{{ $t('URBANRENEWAL_SELECT.RESET.CONFIRM_PWD') }}</label>
              <div class="ur-login-field-row">
                <span class="ur-login-field-icon">
                  <!-- Lock icon -->
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="5" y="11" width="14" height="10" rx="2"/>
                    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                  </svg>
                </span>
                <input
                  class="ur-login-field-input"
                  :type="showPwd ? 'text' : 'password'"
                  v-model="confirmPassword"
                  autocomplete="new-password"
                  @keyup.enter="submit"
                  @input="errorMsg = ''"
                />
              </div>
            </div>

            <!-- Submit button -->
            <button
              class="ur-login-submit-btn"
              type="button"
              :disabled="!canSubmit"
              @click="submit"
            >
              {{ $t('URBANRENEWAL_SELECT.RESET.SUBMIT') }}
            </button>
          </template>

          <template v-else>
            <div class="ur-sel-success">{{ successMsg }}</div>
          </template>

          <!-- Back to login -->
          <div class="ur-login-forgot-wrap">
            <button class="ur-login-forgot-link" type="button" @click.prevent="goLogin">
              {{ $t('URBANRENEWAL_SELECT.RESET.BACK_LOGIN') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Footer ── -->
    <footer class="ur-login-footer">
      {{ $t('URBANRENEWAL_SELECT.LOGIN.COPYRIGHT') }}
      <span class="ur-login-footer-sep">|</span>
      <a class="ur-login-footer-link" href="/docs/DPF.pdf" target="_blank" rel="noopener">Data Processing Agreement</a>
    </footer>
  </div>
</template>

<script>
import UrbanRenewalSelectService from '@/services/UrbanRenewalSelectService';
import LoginValidationService from '@/services/LoginValidationService';

export default {
  name: 'UrbanRenewalSelectResetPassword',

  data() {
    return {
      password: '',
      confirmPassword: '',
      showPwd: false,
      submitting: false,
      errorMsg: '',
      successMsg: '',
      logoUrl: require('@/assets/images/UPGPT.png'),
    };
  },

  mounted() {
    if (this.tenant) {
      LoginValidationService.getUrbanRenewalLogo(this.tenant)
        .then(res => {
          if (res.data && res.data.logo_url) {
            this.logoUrl = res.data.logo_url;
          }
        })
        .catch(() => {});
    }
    if (!this.token) {
      this.errorMsg = this.$t('URBANRENEWAL_SELECT.RESET.ERR_INVALID');
    }
  },

  computed: {
    token() {
      return this.$route.query.token || '';
    },
    tenant() {
      return this.$route.query.tenant || window.localStorage.getItem('tenant') || '';
    },
    canSubmit() {
      return !!this.token && this.password.trim() && this.confirmPassword.trim() && !this.submitting;
    },
  },

  methods: {
    async submit() {
      if (!this.token) {
        this.errorMsg = this.$t('URBANRENEWAL_SELECT.RESET.ERR_INVALID');
        return;
      }
      if (!this.password.trim()) {
        this.errorMsg = this.$t('URBANRENEWAL_SELECT.RESET.ERR_REQUIRED');
        return;
      }
      if (this.password !== this.confirmPassword) {
        this.errorMsg = this.$t('URBANRENEWAL_SELECT.RESET.ERR_MISMATCH');
        return;
      }
      this.submitting = true;
      this.errorMsg = '';
      try {
        await UrbanRenewalSelectService.resetPassword(this.token, this.password);
        this.successMsg = this.$t('URBANRENEWAL_SELECT.RESET.SUCCESS');
        setTimeout(() => {
          this.goLogin();
        }, 1500);
      } catch (e) {
        const d = e.response && e.response.data ? e.response.data : {};
        this.errorMsg = d.detail || this.$t('URBANRENEWAL_SELECT.RESET.ERR_INVALID');
      } finally {
        this.submitting = false;
      }
    },

    togglePwd() {
      this.showPwd = !this.showPwd;
    },

    goLogin() {
      this.$router.push({
        path: '/urbanrenewal/select/login',
        query: this.tenant ? { tenant: this.tenant } : {},
      });
    },
  },
};
</script>
